import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  Award, Plus, Phone, Mail, MapPin, Building2, Users, 
  DollarSign, Clock, Trash2, UserPlus, Briefcase, Shield
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useDivision } from '@/context/DivisionContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEPARTMENTS = ['Corporate', 'Field', 'Estimating', 'Accounting', 'Operations', 'Other'];
const COMPANY_SIZES = [
  { value: 'small', label: 'Small (< 50 employees)' },
  { value: 'mid', label: 'Mid-size (50-250 employees)' },
  { value: 'large', label: 'Large (250+ employees)' }
];

const Contractors = () => {
  const { getAuthHeaders } = useAuth();
  const { division } = useDivision();
  const navigate = useNavigate();
  const [contractors, setContractors] = useState([]);
  const [filteredContractors, setFilteredContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  
  // Enhanced contractor state
  const [newContractor, setNewContractor] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    years_in_business: '',
    company_size: '',
    annual_revenue: '',
    service_areas: [],
    notes: '',
    contacts: []
  });
  
  // State for adding contacts
  const [newContact, setNewContact] = useState({
    name: '',
    title: '',
    department: '',
    phone: '',
    email: '',
    is_primary: false
  });

  useEffect(() => {
    fetchContractors();
  }, [division]);

  useEffect(() => {
    if (searchTerm) {
      setFilteredContractors(
        contractors.filter(c =>
          c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (c.contacts && c.contacts.some(contact => 
            contact.name?.toLowerCase().includes(searchTerm.toLowerCase())
          )) ||
          (c.city && c.city.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      );
    } else {
      setFilteredContractors(contractors);
    }
  }, [searchTerm, contractors]);

  const fetchContractors = async () => {
    try {
      const response = await axios.get(`${API}/contractors`, {
        headers: getAuthHeaders(),
        params: { division }
      });
      setContractors(response.data);
      setFilteredContractors(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching contractors:', error);
      toast.error('Failed to load contractors');
      setLoading(false);
    }
  };

  const addContact = () => {
    if (!newContact.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    
    // If this is the first contact or marked as primary, set as primary
    const isPrimary = newContractor.contacts.length === 0 || newContact.is_primary;
    
    // If setting as primary, remove primary from others
    const updatedContacts = isPrimary 
      ? newContractor.contacts.map(c => ({ ...c, is_primary: false }))
      : [...newContractor.contacts];
    
    setNewContractor({
      ...newContractor,
      contacts: [...updatedContacts, { ...newContact, is_primary: isPrimary }]
    });
    
    setNewContact({
      name: '',
      title: '',
      department: '',
      phone: '',
      email: '',
      is_primary: false
    });
    
    toast.success('Contact added');
  };

  const removeContact = (index) => {
    const updatedContacts = newContractor.contacts.filter((_, i) => i !== index);
    // If we removed the primary contact and there are still contacts, make the first one primary
    if (newContractor.contacts[index]?.is_primary && updatedContacts.length > 0) {
      updatedContacts[0].is_primary = true;
    }
    setNewContractor({ ...newContractor, contacts: updatedContacts });
  };

  const handleAddContractor = async () => {
    if (!newContractor.name.trim()) {
      toast.error('Contractor name is required');
      return;
    }

    try {
      const payload = {
        name: newContractor.name,
        address: newContractor.address || null,
        city: newContractor.city || null,
        state: newContractor.state || null,
        zip_code: newContractor.zip_code || null,
        years_in_business: newContractor.years_in_business ? parseInt(newContractor.years_in_business) : null,
        company_size: newContractor.company_size || null,
        annual_revenue: newContractor.annual_revenue ? parseFloat(newContractor.annual_revenue) : null,
        service_areas: newContractor.service_areas.length > 0 ? newContractor.service_areas : null,
        notes: newContractor.notes || null,
        contacts: newContractor.contacts.length > 0 ? newContractor.contacts : null
      };

      await axios.post(`${API}/contractors`, payload, {
        headers: getAuthHeaders(),
        params: { division }
      });
      toast.success('Contractor added successfully!');
      setAddDialogOpen(false);
      resetForm();
      fetchContractors();
    } catch (error) {
      console.error('Error adding contractor:', error);
      toast.error('Failed to add contractor');
    }
  };

  const resetForm = () => {
    setNewContractor({
      name: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      years_in_business: '',
      company_size: '',
      annual_revenue: '',
      service_areas: [],
      notes: '',
      contacts: []
    });
    setNewContact({
      name: '',
      title: '',
      department: '',
      phone: '',
      email: '',
      is_primary: false
    });
  };

  const viewContractorProfile = (contractorId) => {
    navigate(`/contractors/${contractorId}`);
  };

  // Get primary contact for display
  const getPrimaryContact = (contractor) => {
    if (contractor.contacts && contractor.contacts.length > 0) {
      return contractor.contacts.find(c => c.is_primary) || contractor.contacts[0];
    }
    // Fallback to legacy fields
    return {
      name: contractor.contact_person,
      phone: contractor.phone,
      email: contractor.email
    };
  };

  if (loading) {
    return (
      <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
        <div className="text-center py-12 text-[#94a3b8]">Loading contractors...</div>
      </div>
    );
  }

  return (
    <div className="ml-64 p-8 min-h-screen bg-gradient-to-br from-[#0f172a] to-[#1e293b]">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-2">
              <span className="text-[#f97316]">Contractors</span>
            </h1>
            <p className="text-[#94a3b8] text-sm uppercase tracking-widest">
              {filteredContractors.length} Contractors in {division === 'texas' ? 'Texas' : 'Louisiana'}
            </p>
          </div>
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="millmetrix-button bg-[#10b981] hover:bg-[#059669] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Contractor
          </Button>
        </div>

        {/* Search */}
        <Card className="millmetrix-card p-6 mb-6">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
            Search Contractors
          </Label>
          <Input
            type="text"
            placeholder="Search by name, contact person, or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="millmetrix-input"
          />
        </Card>

        {/* Contractors Grid */}
        {filteredContractors.length === 0 ? (
          <Card className="millmetrix-card p-12 text-center">
            <Award className="w-16 h-16 text-[#94a3b8] mx-auto mb-4" />
            <p className="text-[#94a3b8] text-lg mb-4">
              {contractors.length === 0 ? 'No contractors yet' : 'No contractors match your search'}
            </p>
            {contractors.length === 0 && (
              <Button
                onClick={() => setAddDialogOpen(true)}
                className="millmetrix-button bg-[#f97316] hover:bg-[#ea580c] text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Contractor
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContractors.map((contractor) => {
              const primaryContact = getPrimaryContact(contractor);
              const contactCount = contractor.contacts?.length || (contractor.contact_person ? 1 : 0);
              
              return (
                <Card
                  key={contractor.id}
                  className="millmetrix-card p-6 cursor-pointer hover:border-[#f97316] transition-all"
                  onClick={() => viewContractorProfile(contractor.id)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white mb-1">{contractor.name}</h3>
                      {contractor.company_size && (
                        <span className="text-xs bg-[#1e293b] text-[#94a3b8] px-2 py-1 rounded">
                          {contractor.company_size.charAt(0).toUpperCase() + contractor.company_size.slice(1)} Company
                        </span>
                      )}
                    </div>
                    <Award className="w-5 h-5 text-[#f97316]" />
                  </div>

                  {/* Primary Contact */}
                  {primaryContact?.name && (
                    <div className="mb-4 p-3 bg-[#0f172a] rounded-lg border border-[rgba(255,255,255,0.05)]">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-[#f97316]" />
                        <span className="text-sm font-semibold text-white">{primaryContact.name}</span>
                        {primaryContact.title && (
                          <span className="text-xs text-[#94a3b8]">• {primaryContact.title}</span>
                        )}
                      </div>
                      <div className="space-y-1">
                        {primaryContact.phone && (
                          <div className="flex items-center gap-2 text-xs text-[#94a3b8]">
                            <Phone className="w-3 h-3" />
                            <span>{primaryContact.phone}</span>
                          </div>
                        )}
                        {primaryContact.email && (
                          <div className="flex items-center gap-2 text-xs text-[#94a3b8]">
                            <Mail className="w-3 h-3" />
                            <span className="truncate">{primaryContact.email}</span>
                          </div>
                        )}
                      </div>
                      {contactCount > 1 && (
                        <p className="text-xs text-[#f97316] mt-2">+{contactCount - 1} more contact{contactCount > 2 ? 's' : ''}</p>
                      )}
                    </div>
                  )}

                  {/* Company Info */}
                  <div className="space-y-2 mb-4">
                    {contractor.city && (
                      <div className="flex items-center gap-2 text-sm text-[#94a3b8]">
                        <MapPin className="w-4 h-4" />
                        <span>{contractor.city}, {contractor.state || ''}</span>
                      </div>
                    )}
                    {contractor.years_in_business && (
                      <div className="flex items-center gap-2 text-sm text-[#94a3b8]">
                        <Clock className="w-4 h-4" />
                        <span>{contractor.years_in_business} years in business</span>
                      </div>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-4 border-[#f97316] text-[#f97316] hover:bg-[#f97316] hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      viewContractorProfile(contractor.id);
                    }}
                  >
                    View Profile
                  </Button>
                </Card>
              );
            })}
          </div>
        )}

        {/* Enhanced Add Contractor Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={(open) => { setAddDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="bg-[#1e293b] text-white border-[rgba(255,255,255,0.1)] max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                <Building2 className="w-6 h-6 text-[#f97316]" />
                Add New Contractor
              </DialogTitle>
              <DialogDescription className="text-[#94a3b8]">
                Enter contractor company details and contacts
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Company Name */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-[#f97316]">
                  Company Name *
                </Label>
                <Input
                  value={newContractor.name}
                  onChange={(e) => setNewContractor({ ...newContractor, name: e.target.value.toUpperCase() })}
                  className="millmetrix-input mt-2 uppercase"
                  placeholder="ABC CONSTRUCTION LLC"
                />
              </div>

              {/* Company Profile Section */}
              <div className="border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-[#94a3b8] mb-4 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Company Profile
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Years in Business
                    </Label>
                    <Input
                      type="number"
                      value={newContractor.years_in_business}
                      onChange={(e) => setNewContractor({ ...newContractor, years_in_business: e.target.value })}
                      className="millmetrix-input mt-2"
                      placeholder="15"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Company Size
                    </Label>
                    <Select
                      value={newContractor.company_size}
                      onValueChange={(value) => setNewContractor({ ...newContractor, company_size: value })}
                    >
                      <SelectTrigger className="millmetrix-input mt-2">
                        <SelectValue placeholder="Select size" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1e293b] border-[rgba(255,255,255,0.1)]">
                        {COMPANY_SIZES.map(size => (
                          <SelectItem key={size.value} value={size.value} className="text-white hover:bg-[#334155]">
                            {size.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Est. Annual Revenue
                    </Label>
                    <Input
                      type="number"
                      value={newContractor.annual_revenue}
                      onChange={(e) => setNewContractor({ ...newContractor, annual_revenue: e.target.value })}
                      className="millmetrix-input mt-2"
                      placeholder="5000000"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Service Areas (comma separated)
                    </Label>
                    <Input
                      value={newContractor.service_areas.join(', ')}
                      onChange={(e) => setNewContractor({ 
                        ...newContractor, 
                        service_areas: e.target.value.split(',').map(s => s.trim()).filter(s => s) 
                      })}
                      className="millmetrix-input mt-2"
                      placeholder="Houston, Austin, Dallas"
                    />
                  </div>
                </div>
              </div>

              {/* Address Section */}
              <div className="border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-[#94a3b8] mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="md:col-span-4">
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Street Address
                    </Label>
                    <Input
                      value={newContractor.address}
                      onChange={(e) => setNewContractor({ ...newContractor, address: e.target.value })}
                      className="millmetrix-input mt-2"
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      City
                    </Label>
                    <Input
                      value={newContractor.city}
                      onChange={(e) => setNewContractor({ ...newContractor, city: e.target.value })}
                      className="millmetrix-input mt-2"
                      placeholder="Houston"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      State
                    </Label>
                    <Input
                      value={newContractor.state}
                      onChange={(e) => setNewContractor({ ...newContractor, state: e.target.value.toUpperCase() })}
                      className="millmetrix-input mt-2"
                      placeholder="TX"
                      maxLength={2}
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      ZIP Code
                    </Label>
                    <Input
                      value={newContractor.zip_code}
                      onChange={(e) => setNewContractor({ ...newContractor, zip_code: e.target.value })}
                      className="millmetrix-input mt-2"
                      placeholder="77001"
                    />
                  </div>
                </div>
              </div>

              {/* Contacts Section */}
              <div className="border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <h3 className="text-sm font-bold uppercase tracking-widest text-[#94a3b8] mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Contacts ({newContractor.contacts.length})
                </h3>

                {/* Existing Contacts */}
                {newContractor.contacts.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {newContractor.contacts.map((contact, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-[#0f172a] rounded-lg border border-[rgba(255,255,255,0.05)]">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-white">{contact.name}</span>
                            {contact.is_primary && (
                              <span className="text-xs bg-[#f97316] text-white px-2 py-0.5 rounded">Primary</span>
                            )}
                            {contact.title && (
                              <span className="text-xs text-[#94a3b8]">• {contact.title}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-[#94a3b8] mt-1">
                            {contact.department && <span>{contact.department}</span>}
                            {contact.phone && <span>📞 {contact.phone}</span>}
                            {contact.email && <span>✉️ {contact.email}</span>}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeContact(index)}
                          className="text-[#f43f5e] hover:bg-[#f43f5e]/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add New Contact Form */}
                <div className="bg-[#0f172a] rounded-lg p-4 border border-[rgba(255,255,255,0.05)]">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-[#94a3b8] mb-3 flex items-center gap-2">
                    <UserPlus className="w-4 h-4" />
                    Add Contact
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Input
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                        className="millmetrix-input"
                        placeholder="Contact Name *"
                      />
                    </div>
                    <div>
                      <Input
                        value={newContact.title}
                        onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                        className="millmetrix-input"
                        placeholder="Title (e.g., Estimator)"
                      />
                    </div>
                    <div>
                      <Select
                        value={newContact.department}
                        onValueChange={(value) => setNewContact({ ...newContact, department: value })}
                      >
                        <SelectTrigger className="millmetrix-input">
                          <SelectValue placeholder="Department" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1e293b] border-[rgba(255,255,255,0.1)]">
                          {DEPARTMENTS.map(dept => (
                            <SelectItem key={dept} value={dept} className="text-white hover:bg-[#334155]">
                              {dept}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Input
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        className="millmetrix-input"
                        placeholder="Phone"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        className="millmetrix-input"
                        placeholder="Email"
                      />
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm text-[#94a3b8] cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newContact.is_primary}
                          onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                          className="w-4 h-4"
                        />
                        Primary
                      </label>
                      <Button
                        type="button"
                        onClick={addContact}
                        size="sm"
                        className="bg-[#10b981] hover:bg-[#059669] text-white"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Notes
                </Label>
                <textarea
                  value={newContractor.notes}
                  onChange={(e) => setNewContractor({ ...newContractor, notes: e.target.value })}
                  className="millmetrix-input mt-2 min-h-[80px] resize-none w-full"
                  placeholder="Additional notes about this contractor..."
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => { setAddDialogOpen(false); resetForm(); }}
                className="border-[#94a3b8] text-[#94a3b8] hover:bg-[#94a3b8] hover:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddContractor}
                className="bg-[#10b981] hover:bg-[#059669] text-white"
              >
                Add Contractor
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Contractors;
